import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  useDeleteMyOrderMutation,
  useFinishBookingUserMutation,
  useOrderDetailsQuery,
} from '../../redux/api/bookingApi';
import {
  canCancel,
  canFinish,
  formatPKR,
  STATUS,
  statusMeta,
  statusStep,
  STATUS_FLOW,
} from './rideConfig';
import RideMap from './RideMap';
import '../../css/ride.css';

const TONE_ICON = {
  searching: 'bi-radar',
  waiting: 'bi-hourglass-split',
  onway: 'bi-car-front',
  arrived: 'bi-geo-alt-fill',
  ontrip: 'bi-flag-fill',
  done: 'bi-check2-circle',
  complete: 'bi-check2-circle',
  canceled: 'bi-x-circle',
};

export default function RideTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, isLoading, isSuccess } = useOrderDetailsQuery(id, { pollingInterval: 5000 });

  const order = data?.order || {};
  const {
    orderStatus,
    googleMap,
    totalAmount,
    driver,
    driverLocation,
    userRecieved,
    createdAt,
    deliveredAt,
  } = order;

  const [cancelArmed, setCancelArmed] = useState(false);
  const [deleteMyOrder, { isLoading: isCanceling }] = useDeleteMyOrderMutation();
  const [finishBookingUser, { isLoading: isFinishing }] = useFinishBookingUserMutation();

  useEffect(() => {
    if (error) toast.error(error?.data?.message || 'Could not load this trip');
  }, [error]);

  const meta = statusMeta(orderStatus);
  const step = statusStep(orderStatus);
  const showComplete = orderStatus === STATUS.COMPLETED || (userRecieved && orderStatus === STATUS.ARRIVED_DEST);

  const route = googleMap?.origin && googleMap?.destination
    ? { origin: googleMap.origin, destination: googleMap.destination }
    : null;
  const pickup = Number.isFinite(googleMap?.originLatitude) && Number.isFinite(googleMap?.originLongitude)
    ? { lat: googleMap.originLatitude, lng: googleMap.originLongitude }
    : null;

  const onCancel = async () => {
    if (!cancelArmed) {
      setCancelArmed(true);
      setTimeout(() => setCancelArmed(false), 4000);
      return;
    }
    try {
      await deleteMyOrder(id).unwrap();
      toast.success('Ride canceled');
      navigate('/ride/rides', { replace: true });
    } catch (e) {
      toast.error(e?.data?.message || 'Could not cancel the ride');
    }
  };

  const onFinish = async () => {
    try {
      await finishBookingUser({ id }).unwrap();
      toast.success('Trip complete — pay cash to the driver');
    } catch (e) {
      toast.error(e?.data?.message || 'Could not finish the trip');
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
        <span className="ride-spinner" />
      </div>
    );
  }

  if (!isSuccess && !order?._id) {
    return (
      <div className="ride-app">
        <div className="ride-topbar">
          <button className="ride-icon-btn" onClick={() => navigate(-1)}><i className="bi bi-chevron-left" /></button>
          <div className="ride-brand-pill"><span>MBZ Rides</span></div>
          <span style={{ width: 44 }} />
        </div>
        <div className="ride-map-area"><RideMap /></div>
        <div className="ride-panel">
          <div className="ride-panel-inner">
            <div className="ride-empty">
              <i className="bi bi-signpost-split" />
              <div className="h">Trip not found</div>
              <div className="p">This booking may have been canceled.</div>
              <Link to="/ride" className="ride-cta mt-4" style={{ display: 'inline-flex', width: 'auto', padding: '12px 24px' }}>Back to home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ride-topbar">
        <button className="ride-icon-btn" onClick={() => navigate('/ride')} aria-label="Home">
          <i className="bi bi-house" />
        </button>
        <div className="ride-brand-pill"><span>{meta.title}</span></div>
        <button className="ride-icon-btn" onClick={() => navigate('/ride/rides')} aria-label="Rides">
          <i className="bi bi-journal-text" />
        </button>
      </div>

      <div className="ride-map-area">
        <RideMap
          route={route}
          pickup={pickup}
          driver={driverLocation?.latitude ? { latitude: driverLocation.latitude, longitude: driverLocation.longitude } : null}
        />
      </div>

      {orderStatus === STATUS.FINDING && (
        <div className="ride-radar">
          <div className="radar-wrap">
            <span className="ring" />
            <span className="ring" />
            <span className="ring" />
            <span className="radar-core"><i className="bi bi-truck" /></span>
          </div>
          <span className="radar-label">Finding your driver…</span>
        </div>
      )}

      <div className="ride-panel">
        <div className="ride-grabber" />
        <div className="ride-panel-inner">
          <div className={`ride-banner tone-${meta.tone}`}>
            <span className="ride-banner-ico"><i className={`bi ${TONE_ICON[meta.tone] || 'bi-info-circle'}`} /></span>
            <div>
              <div className="ride-banner-title">{meta.title}</div>
              {meta.sub && <div className="ride-banner-sub">{meta.sub}</div>}
              {step >= 0 && (
                <div className="ride-stepdots" aria-label="trip progress">
                  {STATUS_FLOW.map((_, i) => (
                    <span
                      key={i}
                      className={`dot ${i + 1 < step ? 'done' : i + 1 === step ? 'active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Driver card once assigned */}
          {driver?.driverName && (
            <div className="ride-driver-card mt-3">
              {driver.avatar && typeof driver.avatar === 'string' && driver.avatar.startsWith('http') ? (
                <img src={driver.avatar} alt="" />
              ) : (
                <span className="ride-row-ico" style={{ width: 52, height: 52, borderRadius: '50%', fontSize: '1.3rem' }}>
                  <i className="bi bi-person-fill" />
                </span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ride-driver-name">{driver.driverName}</div>
                <div className="ride-driver-vehicle">{driver.driverVehicleMakeModel || 'MBZ partner'}</div>
                {driver.driverVehicleNumber && <span className="ride-plate mt-1">{driver.driverVehicleNumber}</span>}
              </div>
              <div className="ride-driver-actions">
                {driver.driverPhoneNo && (
                  <a className="ride-icon-btn" href={`tel:+92${driver.driverPhoneNo}`} aria-label="Call driver">
                    <i className="bi bi-telephone-fill" />
                  </a>
                )}
                {googleMap?.origin && (
                  <a
                    className="ride-icon-btn"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(googleMap.origin)}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open pickup in Maps"
                  >
                    <i className="bi bi-diamond" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Trip meta */}
          <div className="ride-summary mt-3">
            <div className="ride-summary-head">
              <div>
                <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.8 }}>Fare</div>
                <div className="amt">{formatPKR(totalAmount)}</div>
              </div>
              <div className="text-end">
                <div style={{ fontSize: '.72rem', opacity: 0.8 }}>{googleMap?.distance}</div>
                <div style={{ fontSize: '.78rem' }}>{createdAt ? new Date(createdAt).toLocaleString() : ''}</div>
              </div>
            </div>
            <div className="ride-summary-row"><span>Pickup</span><b>{googleMap?.origin}</b></div>
            <div className="ride-summary-row"><span>Dropoff</span><b>{googleMap?.destination}</b></div>
            {deliveredAt && (
              <div className="ride-summary-row"><span>Completed</span><b>{new Date(deliveredAt).toLocaleString()}</b></div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-4 d-flex flex-column gap-2">
            {showComplete && (
              <>
                <button className="ride-cta accent" onClick={() => navigate('/ride')}>
                  <i className="bi bi-plus-circle" /> Book another ride
                </button>
                <Link to="/ride/rides" className="ride-cta ghost" style={{ textDecoration: 'none' }}>
                  View my rides
                </Link>
              </>
            )}
            {canFinish(orderStatus, userRecieved) && (
              <button className="ride-cta accent" onClick={onFinish} disabled={isFinishing}>
                {isFinishing ? 'Finishing…' : "I've arrived — finish trip"}
              </button>
            )}
            {canCancel(orderStatus) && (
              <button
                className="ride-cta danger-ghost"
                onClick={onCancel}
                disabled={isCanceling}
              >
                {isCanceling ? 'Canceling…' : cancelArmed ? 'Tap again to confirm cancel' : 'Cancel ride'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
